import { SchemaOrgNode } from '@core-mkt/services/schema/schema-org-node.model';
import { canonicalIdBase } from '@core-mkt/services/schema/schema-id.util';
import type { TabContent, TabInfo } from './tabbed-content';

/**
 * Type guard to check if a value is a SafeValue object
 * SafeValue objects have a private property 'changingThisBreaksApplicationSecurity'
 * that contains the actual HTML content
 */
interface SafeValue {
  changingThisBreaksApplicationSecurity?: string;
}

function isSafeValue(value: unknown): value is SafeValue {
  return (
    value !== null &&
    value !== undefined &&
    typeof value === 'object' &&
    'changingThisBreaksApplicationSecurity' in value
  );
}

/**
 * Extracts a raw HTML string from a SafeValue object or plain string.
 * WysiwygRedactorService.bypassSanitizer() returns SafeHtml objects incorrectly cast as strings.
 * We access the internal property to get the actual HTML for schema generation.
 */
function extractHtmlFromSafeValue(html: unknown): string {
  if (!html) return '';
  if (typeof html === 'string') return html;
  if (isSafeValue(html) && html.changingThisBreaksApplicationSecurity) {
    return html.changingThisBreaksApplicationSecurity;
  }
  return '';
}

/**
 * Strips HTML from a string (or SafeValue) for use in schema text fields.
 * All HTML tags are stripped (keeping their text content) so link labels and
 * other inline markup remain readable in schema output. The one exception is
 * sr-only spans injected by WysiwygRedactorService for aria context — those
 * are removed including their content to prevent duplicated text.
 * All remaining tags are replaced with a space to prevent adjacent inline
 * elements from concatenating without whitespace. Spaces that land directly
 * before punctuation are removed so the output reads naturally.
 * This is SSR-safe — it does not depend on DOM APIs.
 */
function stripHtml(html: unknown): string {
  const htmlString = extractHtmlFromSafeValue(html);
  if (!htmlString) return '';
  return htmlString
    .replace(/<span[^>]*class="sr-only"[^>]*>[\s\S]*?<\/span>/gi, '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/ ([.,!?;:])/g, '$1')
    .trim();
}

/**
 * Builds a HowTo Schema.org node from a tab containing step-by-step content.
 * Returns null when no valid steps (title + text) are found.
 */
function getHowToSchema(tab: TabInfo, idBase: string): SchemaOrgNode | null {
  const items = tab.accordion?.accordionItems ?? [];
  const steps = items
    .map((item, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: stripHtml(item.itemTitle || ''),
      text: stripHtml(item.itemDescription || ''),
    }))
    .filter((step) => step.name && step.text);

  if (steps.length === 0) return null;

  return {
    '@type': 'HowTo',
    '@id': `${idBase}#tabbed-content-howto`,
    name: stripHtml(tab.tabTitle || ''),
    step: steps,
  };
}

/**
 * Builds a WebPageElement Schema.org node for general (non-FAQ) tabbed content.
 */
function getGeneralSchema(data: TabContent, idBase: string): SchemaOrgNode {
  return {
    '@type': 'WebPageElement',
    '@id': `${idBase}#tabbed-content`,
    name: data.tabbedContentTitle ? stripHtml(data.tabbedContentTitle) : 'Tabbed Content',
    description: data.tab
      .map((tab) => stripHtml(tab.tabTitle || ''))
      .filter(Boolean)
      .join(', '),
  };
}

/**
 * Returns Schema.org node(s) for a tabbed-content component.
 *
 * All structured data is registered SSR-only via SchemaService and merged into
 * the single consolidated <script type="application/ld+json"> in <head>.
 *
 * - Any tab with accordion Q&A items → FAQPage node, plus an optional HowTo
 *   node when a tab title contains "how" and has valid steps.
 * - No valid Q&A items found → WebPageElement node.
 *
 * Every node carries a stable, page-scoped `@id` (canonical page URL + a
 * component-namespaced fragment) so ServerSchemaService's first-write-wins
 * dedup can coalesce duplicates if the component registers more than once.
 *
 * This is a pure function — no side effects, no DOM access, SSR-safe.
 */
export function getTabbedContentSchema(data: TabContent, pageUrl: string): SchemaOrgNode | SchemaOrgNode[] | null {
  if (!data) return null;

  const idBase = canonicalIdBase(pageUrl);

  // Resolve the HowTo tab first. Its accordion items are step-by-step content that
  // feeds the HowTo node — they must NOT also be emitted as FAQ questions, or the
  // FAQPage fills with "Step N | ..." pseudo-questions (EPS-4070).
  const howToTab = data.tab.find((tab) =>
    stripHtml(tab.tabTitle || '')
      .toLowerCase()
      .includes('how'),
  );
  const howToNode = howToTab ? getHowToSchema(howToTab, idBase) : null;

  // Collect Q&A pairs from every tab EXCEPT the one that became a HowTo node.
  const mainEntity: SchemaOrgNode[] = [];

  data.tab.forEach((tab) => {
    if (howToNode && tab === howToTab) return;
    if (tab.accordion?.accordionItems) {
      tab.accordion.accordionItems.forEach((item) => {
        const name = stripHtml(item.itemTitle);
        const text = stripHtml(item.itemDescription);
        if (name && text) {
          mainEntity.push({
            '@type': 'Question',
            name,
            acceptedAnswer: {
              '@type': 'Answer',
              text,
            },
          });
        }
      });
    }
  });

  if (mainEntity.length > 0) {
    const faqNode: SchemaOrgNode = {
      '@type': 'FAQPage',
      '@id': `${idBase}#tabbed-content-faq`,
      mainEntity,
    };

    return howToNode ? [faqNode, howToNode] : faqNode;
  }

  // No FAQ questions. Emit the HowTo node if we built one; otherwise a generic
  // WebPageElement so the component still contributes structured data.
  return howToNode ?? getGeneralSchema(data, idBase);
}
